/**
 * The application's polite live region (task 9.11).
 *
 * Exactly one, mounted for the life of the application: assistive technology
 * only announces changes to a region that was already in the accessibility tree,
 * so a region rendered at the moment of the news is a region that says nothing.
 * Announcements arrive through `useViewStore().announce`, and never steal focus
 * (`game-shell` spec, "Resolution announced").
 */
import { useEffect, useState, type JSX } from 'react';
import { useViewStore } from '../store/view-store.js';

export function LiveRegion(): JSX.Element {
  const announcement = useViewStore((state) => state.announcement);
  const [text, setText] = useState('');

  useEffect(() => {
    if (!announcement) {
      setText('');
      return;
    }
    // Clearing first guarantees a change to announce even when the same text
    // arrives twice — two identical resolutions read as two, not one.
    setText('');
    const handle = globalThis.setTimeout(() => {
      setText(announcement.text);
    }, 50);
    return () => {
      globalThis.clearTimeout(handle);
    };
  }, [announcement]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="live-region"
      className="sr-only"
    >
      {text}
    </div>
  );
}
